import { Grid, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import BarChart from "../../components/charts/BarChart";
import SideInfo from "./SideInfo";
import AppCard from "../../components/cards/AppCard";
import { PeriodFilter } from "../../common/enums/common.enums";
import { convertChartData } from "../../common/helpers/chart.helper";
import { convertStatisticData } from "../../common/helpers/statistics.helper";
import { IChart, IChartInfo } from "../../common/interfaces/chart.interface";
import { IStatistics } from "../../common/interfaces/statistic.interface";
import { useTabContext } from "../../context/tabs/TabContext";
import {
  getUsersOverview,
  getUsersChartInfo,
} from "../../api/platform/platform.users";
import { IUsersOverview } from "../../common/interfaces/user.interface";

const PlatformUsers: FC = () => {
  const [usersOverview, setUsersOverview] = useState<IUsersOverview>();
  const [usersChartInfo, setUsersChartInfo] = useState<IChart[]>();
  const [usersStatisticInfo, setUsersStatisticInfo] = useState<IStatistics>();

  const [cardLoader, setCardLoader] = useState<boolean>(false);
  const [chartTab, setChartTab] = useState<string>(PeriodFilter.DAILY);

  const changeChartTab = (tab: PeriodFilter): void => {
    setChartTab(tab);
  };

  useEffect(() => {
    void getUsersOverviewFromApi();
    void getUsersChartInfoFromApi();
  }, []);

  const getUsersOverviewFromApi = async () => {
    try {
      setCardLoader(true);
      setUsersOverview(await getUsersOverview());
      setCardLoader(false);
    } catch (error) {
      console.log(error);
    }
  };

  const getUsersChartInfoFromApi = async () => {
    try {
      const response: IChartInfo = await getUsersChartInfo();
      console.log(response);
      const convertedChartData: IChart[] | undefined =
        convertChartData(response);
      setUsersChartInfo(convertedChartData);
      const statisticData = convertStatisticData(response);
      setUsersStatisticInfo(statisticData);
    } catch (error) {
      console.log(error);
    }
  };

  const sumOfActiveUsers = useMemo(() => {
    return usersOverview && usersOverview.active;
  }, [usersOverview]);

  const totalActiveUsers = useMemo(() => {
    return usersOverview && usersOverview.total;
  }, [usersOverview]);

  const founder = useMemo(() => {
    return usersOverview && usersOverview.founder + usersOverview.founder;
  }, [usersOverview]);

  const manager = useMemo(() => {
    return usersOverview && usersOverview.manager + usersOverview.manager;
  }, [usersOverview]);

  const member = useMemo(() => {
    return usersOverview && usersOverview.member + usersOverview.member;
  }, [usersOverview]);

  return (
    <div>
      <Grid
        container
        spacing={{ xs: 1, md: 2, sm: 2 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        <Typography
          variant="h4"
          component="span"
          key={`typography1`}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            borderImageSlice: 1,
            padding: 1,
            width: "100%",
          }}
        >
          Users
        </Typography>
        <Grid item xs={12} sm={12} md={4.5}>
          <AppCard
            label={"Active users / Total users"}
            value={`${sumOfActiveUsers} / ${totalActiveUsers}`}
            loader={cardLoader}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={2.5}>
          <AppCard label={"Founder"} value={`${founder}`} loader={cardLoader} />
        </Grid>
        <Grid item xs={12} sm={12} md={2.5}>
          <AppCard label={"Manager"} value={`${manager}`} loader={cardLoader} />
        </Grid>
        <Grid item xs={12} sm={12} md={2.5}>
          <AppCard label={"Member"} value={`${member}`} loader={cardLoader} />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={{ xs: 1, md: 2, sm: 2 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        <Grid item xs={12} sm={12} md={8}>
          <useTabContext.Provider
            value={{
              tab: chartTab,
              changeTab: changeChartTab,
            }}
          >
            <SideInfo type={"users"} statisticsData={usersStatisticInfo} />
            <BarChart chartData={usersChartInfo} />
          </useTabContext.Provider>
        </Grid>
      </Grid>
    </div>
  );
};

export default PlatformUsers;
